import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, PackagePlus, AlertCircle } from 'lucide-react';
import InboundItemsTable from '../InboundItemsTable';
import subcontractService from '../../services/subcontractService';
import productionService from '../../services/productionService';

const SubcontractInboundDialog = ({
  open,
  onClose,
  order,
  onSuccess
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState(['main']);
  const [defaultLocation, setDefaultLocation] = useState('main');
  const [notes, setNotes] = useState('');
  const [inboundItems, setInboundItems] = useState([]);

  useEffect(() => {
    if (open && order?.items) {
      const initialItems = order.items.map(item => {
        const alreadyReceived = item.received_quantity || 0;
        return {
          id: item.item_id,
          item_id: item.item_id,
          product_code: item.product_code || item.finished_product_code,
          product_name: item.product_name || item.finished_product_name,
          planned_quantity: item.planned_quantity || 0,
          already_received: alreadyReceived,
          quantity: Math.max(0, (item.planned_quantity || 0) - alreadyReceived),
          use_custom_location: false,
          location: 'main'
        };
      });
      setInboundItems(initialItems);
      setNotes('');
      setDefaultLocation('main');
      setError(null);
      loadWarehouseLocations();
    }
  }, [open, order]);

  const loadWarehouseLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await productionService.getMaterialWarehouseLocations();
      const locations = response.locations || ['main'];
      setWarehouses(locations);
      setDefaultLocation(locations[0] || 'main');
    } catch (err) {
      console.warn('Failed to load warehouse locations:', err);
      setWarehouses(['main']);
      setDefaultLocation('main');
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleDefaultLocationChange = (location) => {
    setDefaultLocation(location);
    setInboundItems(prev =>
      prev.map(item =>
        item.use_custom_location ? item : { ...item, location }
      )
    );
  };

  const handleItemChange = (id, field, value) => {
    setInboundItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item;

        if (field === 'use_custom_location') {
          return {
            ...item,
            use_custom_location: value,
            location: value ? item.location : defaultLocation
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const totalInbound = inboundItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const handleSubmit = async () => {
    const validItems = inboundItems.filter(item => (item.quantity || 0) > 0);
    if (validItems.length === 0) {
      setError('请至少填写一个入库数量');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payloadItems = validItems.map(item => ({
        item_id: item.item_id,
        quantity: item.quantity,
        location: item.use_custom_location ? item.location : defaultLocation
      }));

      const result = await subcontractService.receiveInbound(
        order.subcontract_order_id,
        payloadItems,
        defaultLocation,
        notes
      );

      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Failed to receive subcontract inbound:', err);
      const errorMsg = err.response?.data?.error || err.message || '外协入库失败';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setNotes('');
    setDefaultLocation('main');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="h-5 w-5 text-green-600" />
            外协成品入库
          </DialogTitle>
          <DialogDescription>
            外协单 {order?.subcontract_order_id} - {order?.supplier_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inbound-location">默认入库仓库</Label>
              <select
                id="inbound-location"
                value={defaultLocation}
                onChange={(e) => handleDefaultLocationChange(e.target.value)}
                disabled={submitting || loadingLocations}
                className="w-full px-2 py-1 border rounded h-10 bg-background"
              >
                {warehouses.map((loc, idx) => (
                  <option key={idx} value={loc}>
                    {loc === 'main' ? '主仓库' : loc}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inbound-notes">备注</Label>
              <Input
                id="inbound-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="入库备注..."
                disabled={submitting}
              />
            </div>
          </div>

          <InboundItemsTable
            items={inboundItems}
            defaultLocation={defaultLocation}
            onDefaultLocationChange={handleDefaultLocationChange}
            onItemChange={handleItemChange}
            disabled={submitting}
            showDefaultLocationInput={false}
            showReceived
            quantityLabel="入库数量"
            title="入库明细"
          />

          {inboundItems.length > 0 && (
            <div className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
              本次入库总计: {totalInbound} 件
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || totalInbound <= 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                提交中...
              </>
            ) : (
              <>确认入库</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubcontractInboundDialog;
